import React, { useState } from 'react';
import { Film, Clock, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Pricing structure based on duration and cameras
const PRICING = {
  '30': { '1_camera': 1000000, '2_cameras': 1400000, '3_cameras': 1800000 },
  '60': { '1_camera': 2000000, '2_cameras': 2800000, '3_cameras': 3600000 }
};

const DURATION_OPTIONS = [
  { value: '30', label: '30 minutes', price_label: 'from 1M IDR' },
  { value: '60', label: '60 minutes', price_label: 'from 2M IDR' },
  { value: 'custom', label: 'More than 60 minutes', price_label: 'Custom quote' }
];

const CAMERA_OPTIONS = [
  { value: '1_camera', label: '1 camera' },
  { value: '2_cameras', label: '2 cameras' },
  { value: '3_cameras', label: '3 cameras' }
];

export function VideoEditingSection({ videoEditing, onChange, formatIDR }) {
  const [isExpanded, setIsExpanded] = useState(videoEditing.enabled);

  const handleToggle = () => {
    const newEnabled = !videoEditing.enabled;
    setIsExpanded(newEnabled);
    onChange({
      ...videoEditing,
      enabled: newEnabled,
      duration: newEnabled ? (videoEditing.duration || '') : '',
      cameras: newEnabled ? (videoEditing.cameras || '1_camera') : '1_camera',
      isCustomQuote: false
    });
  };

  const handleDurationChange = (duration) => {
    const isCustom = duration === 'custom';
    onChange({
      ...videoEditing,
      duration: duration,
      isCustomQuote: isCustom,
      cameras: isCustom ? '' : (videoEditing.cameras || '1_camera')
    });
  };

  const handleCamerasChange = (cameras) => {
    onChange({ ...videoEditing, cameras });
  };

  const calculatePrice = () => {
    if (!videoEditing.enabled || !videoEditing.duration || videoEditing.isCustomQuote) return 0;
    
    const durationPricing = PRICING[videoEditing.duration];
    if (!durationPricing) return 0;
    
    return durationPricing[videoEditing.cameras] || 0;
  };

  const currentPrice = calculatePrice();

  return (
    <div 
      className="p-6 border border-border hover:border-primary/50 transition-colors"
      data-testid="video-editing-section"
    >
      {/* Header with toggle */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${videoEditing.enabled ? 'bg-primary/20' : 'bg-muted'}`}>
            <Film className={`w-6 h-6 ${videoEditing.enabled ? 'text-primary' : 'text-foreground-muted'}`} />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Full Video Editing</h3>
            <p className="text-sm text-foreground-muted">Podcast / Interview • English content only</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {videoEditing.enabled && currentPrice > 0 && (
            <span className="text-primary font-semibold" data-testid="video-editing-price">
              {formatIDR(currentPrice)}
            </span>
          )}
          {videoEditing.enabled && videoEditing.isCustomQuote && (
            <span className="text-amber-500 font-semibold text-sm">Custom quote</span>
          )}
          <button
            type="button"
            className={`w-12 h-7 rounded-full transition-colors duration-300 relative ${
              videoEditing.enabled ? 'bg-primary' : 'bg-muted'
            }`}
            data-testid="video-editing-toggle"
          >
            <span 
              className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${
                videoEditing.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Options conditionnelles */}
      <AnimatePresence>
        {videoEditing.enabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-6 pt-6 border-t border-border space-y-6">
              
              {/* Step 1: Estimated video duration */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  1. Estimated duration of your final video
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {DURATION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDurationChange(option.value);
                      }}
                      className={`p-4 border-2 transition-all text-center ${
                        videoEditing.duration === option.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`video-duration-${option.value}`}
                    >
                      <span className="block font-semibold">{option.label}</span>
                      <span className={`text-xs ${option.value === 'custom' ? 'text-amber-500' : 'text-foreground-muted'}`}>
                        {option.price_label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message for more than 60 minutes */}
              {videoEditing.isCustomQuote && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-amber-500/10 border border-amber-500/30"
                >
                  <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-amber-200 font-semibold mb-1">Custom quote required</p>
                      <p className="text-sm text-foreground-muted">
                        For videos longer than 60 minutes, pricing is provided on request. 
                        You can contact us by message or discuss directly on the shooting day.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Number of cameras (only if duration selected and not custom) */}
              {videoEditing.duration && !videoEditing.isCustomQuote && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <label className="text-sm font-semibold text-foreground mb-3 block">
                    2. Number of cameras to edit
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {CAMERA_OPTIONS.map((option) => {
                      const price = PRICING[videoEditing.duration]?.[option.value] || 0;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCamerasChange(option.value);
                          }}
                          className={`p-4 border-2 transition-all text-center ${
                            videoEditing.cameras === option.value
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`video-cameras-${option.value}`}
                        >
                          <span className="block font-semibold">{option.label}</span>
                          <span className="text-xs text-primary font-bold">{formatIDR(price)}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Summary and delivery time */}
              {videoEditing.duration && !videoEditing.isCustomQuote && videoEditing.cameras && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="p-4 bg-muted/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold">Editing summary</span>
                    <span className="text-lg font-bold text-primary">{formatIDR(currentPrice)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground-muted">
                    <Clock className="w-4 h-4" />
                    <span>Delivery time: <strong className="text-foreground">3 to 7 business days</strong></span>
                  </div>
                </motion.div>
              )}

              {/* Delivery for custom quote */}
              {videoEditing.isCustomQuote && (
                <div className="p-4 bg-muted/50 border border-border">
                  <div className="flex items-center gap-2 text-sm text-foreground-muted">
                    <Clock className="w-4 h-4" />
                    <span>Estimated delivery time: <strong className="text-foreground">3 to 7 business days</strong> (depending on complexity)</span>
                  </div>
                </div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
